import React from "react";
import { Link } from "react-router-dom";

export default function PlatformHero() {
  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-black">
      
      {/* Mobile Animated Background - visible only on mobile via CSS */}
      <div className="absolute inset-0 overflow-hidden md:hidden">
        {/* Base dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0a0a] via-[#0d0d0d] to-black" />
        
        {/* Animated red gradient sweep */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(125deg, transparent 0%, transparent 40%, #B91C1C 50%, transparent 60%, transparent 100%)',
            backgroundSize: '200% 200%',
            animation: 'gradientSweep 4s ease-in-out infinite'
          }}
        />
        
        {/* Pulsing center glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-gradient-radial from-secondary/30 via-secondary/10 to-transparent animate-pulse" 
          style={{ filter: 'blur(60px)' }} 
        />
        
        {/* Moving light beams */}
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-20"
          style={{
            background: 'conic-gradient(from 0deg at 50% 50%, transparent 0deg, #B91C1C 30deg, transparent 60deg, transparent 180deg, #B91C1C 210deg, transparent 240deg)',
            animation: 'spin 20s linear infinite'
          }}
        />
        
        {/* Floating house icons */}
        <div className="absolute inset-0">
          <div className="absolute top-[15%] left-[10%] text-4xl opacity-20 animate-float" style={{ animationDelay: '0s' }}>🏠</div>
          <div className="absolute top-[25%] right-[15%] text-3xl opacity-15 animate-float" style={{ animationDelay: '1s' }}>🏗️</div>
          <div className="absolute top-[60%] left-[20%] text-3xl opacity-15 animate-float" style={{ animationDelay: '2s' }}>🏡</div>
          <div className="absolute top-[70%] right-[10%] text-4xl opacity-20 animate-float" style={{ animationDelay: '0.5s' }}>🏘️</div>
          <div className="absolute top-[40%] left-[5%] text-2xl opacity-10 animate-float" style={{ animationDelay: '1.5s' }}>🔑</div>
          <div className="absolute top-[50%] right-[25%] text-2xl opacity-10 animate-float" style={{ animationDelay: '2.5s' }}>📍</div>
        </div>
        
        {/* Animated particles rising */}
        <div className="absolute bottom-0 left-0 right-0 h-full overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{
                left: `${8 + i * 8}%`,
                bottom: '-10px',
                opacity: 0.4 + (i % 3) * 0.2,
                animation: `rise ${3 + (i % 3)}s ease-out infinite`,
                animationDelay: `${i * 0.3}s`
              }}
            />
          ))}
        </div>
        
        {/* Bottom city silhouette */}
        <div className="absolute bottom-0 left-0 right-0 h-24">
          <svg viewBox="0 0 400 60" className="w-full h-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="cityGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#B91C1C" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#B91C1C" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <path 
              d="M0,60 L0,45 L15,45 L15,35 L25,35 L25,25 L35,25 L35,45 L50,45 L50,30 L60,20 L70,30 L70,45 L85,45 L85,40 L95,40 L95,45 L110,45 L110,25 L125,10 L140,25 L140,45 L155,45 L155,35 L165,35 L165,45 L180,45 L180,30 L195,15 L210,30 L210,45 L225,45 L225,38 L235,38 L235,45 L250,45 L250,28 L260,18 L270,28 L270,45 L285,45 L285,42 L295,42 L295,45 L310,45 L310,32 L325,22 L340,32 L340,45 L355,45 L355,40 L365,40 L365,45 L380,45 L380,35 L390,35 L390,45 L400,45 L400,60 Z" 
              fill="url(#cityGrad)"
            />
          </svg>
        </div>
        
        {/* Ambient corner glows */}
        <div className="absolute top-0 left-0 w-48 h-48 bg-red-600/20 rounded-full blur-3xl" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/15 rounded-full blur-2xl" />
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-red-500/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-32 right-5 w-36 h-36 bg-secondary/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Custom CSS animations */}
      <style>{`
        @keyframes gradientSweep {
          0%, 100% { background-position: 0% 0%; }
          50% { background-position: 100% 100%; }
        }
        @keyframes rise {
          0% { transform: translateY(0) scale(1); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.3; }
          100% { transform: translateY(-500px) scale(0.5); opacity: 0; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Desktop Video Background - hidden on mobile via CSS */}
      <video
        src="/hero_video.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.35] hidden md:block"
        poster="/galeria1_ai.jpg"
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black" />
      
      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center px-4 py-8 md:py-12 max-w-7xl mx-auto">
        
        {/* Badge */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/20 to-red-600/20 backdrop-blur-xl border border-secondary/30 rounded-full px-6 py-3 shadow-2xl shadow-secondary/20">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-secondary"></span>
            </span>
            <span className="text-secondary font-bold text-sm tracking-wider">🏠 OTTHONOK PLATFORMJA</span>
          </div>
        </div>
        
        {/* Main Title */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 text-center tracking-tight leading-tight">
          <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
            HOPPhomes
          </span>
        </h1>
        
        {/* Provocative Subtitle */}
        <p className="text-2xl md:text-4xl lg:text-5xl text-white mb-6 text-center font-light leading-tight">
          Az építkezések nem azért csúsznak,
        </p>
        <p className="text-2xl md:text-4xl lg:text-5xl mb-10 text-center font-bold leading-tight">
          <span className="bg-gradient-to-r from-secondary via-red-500 to-secondary bg-clip-text text-transparent">mert te rosszul döntesz.</span>
        </p>
        
        {/* Strong Description */}
        <p className="text-lg md:text-2xl text-gray-300 mb-12 text-center max-w-3xl leading-relaxed">
          A legtöbb probléma nem a háznál kezdődik – hanem a rendszer hiányánál.
        </p>
        <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-xl border border-green-500/30 rounded-2xl px-8 py-4 mb-12 shadow-2xl">
          <p className="text-xl md:text-2xl font-bold text-white text-center">
            Mi már végiggondoltuk helyetted.
          </p>
        </div>
        
        {/* 4 CTA Buttons - Main Navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 w-full max-w-4xl mb-12">
          
          {/* Házat szeretnék építeni */}
          <Link 
            to="/egyedi-hazak"
            className="group relative overflow-hidden flex items-center gap-4 bg-gradient-to-r from-secondary/20 to-red-600/20 hover:from-secondary hover:to-red-600 backdrop-blur-xl border-2 border-secondary/40 hover:border-secondary rounded-2xl px-6 py-6 transition-all duration-300 no-underline hover:scale-105 shadow-xl shadow-secondary/20 hover:shadow-2xl hover:shadow-secondary/40"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
            <span className="text-4xl md:text-5xl relative z-10">🏗️</span>
            <div className="text-left relative z-10">
              <span className="block text-white font-black text-lg md:text-xl mb-1">Építeni akarok</span>
              <span className="block text-gray-300 text-sm group-hover:text-white">Mutasd a rendszert →</span>
            </div>
          </Link>
          
          {/* Kész házat keresek */}
          <Link 
            to="/kesz-hazak"
            className="group flex items-center gap-4 bg-white/10 hover:bg-gradient-to-r hover:from-green-600 hover:to-emerald-600 backdrop-blur-xl border-2 border-white/[0.08] hover:border-green-500 rounded-2xl px-6 py-6 transition-all duration-300 no-underline hover:scale-105 shadow-xl hover:shadow-2xl hover:shadow-green-500/30"
          >
            <span className="text-4xl md:text-5xl">🏠</span>
            <div className="text-left">
              <span className="block text-white font-black text-lg md:text-xl mb-1">Kész házat keresek</span>
              <span className="block text-gray-400 text-sm group-hover:text-white">Azonnal költözhető</span>
            </div>
          </Link>
          
          {/* Mobilház érdekel */}
          <Link 
            to="/mobilhazak"
            className="group flex items-center gap-4 bg-white/10 hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-600 backdrop-blur-xl border-2 border-white/[0.08] hover:border-blue-500 rounded-2xl px-6 py-6 transition-all duration-300 no-underline hover:scale-105 shadow-xl hover:shadow-2xl hover:shadow-blue-500/30"
          >
            <span className="text-4xl md:text-5xl">🚐</span>
            <div className="text-left">
              <span className="block text-white font-black text-lg md:text-xl mb-1">Mobilház kell</span>
              <span className="block text-gray-400 text-sm group-hover:text-white">Gyors, fix áras</span>
            </div>
          </Link>
          
          {/* Eladó / kiadó ingatlanom van */}
          <Link 
            to="/ingatlan-piac"
            className="group flex items-center gap-4 bg-white/10 hover:bg-gradient-to-r hover:from-yellow-600 hover:to-amber-600 backdrop-blur-xl border-2 border-white/[0.08] hover:border-yellow-500 rounded-2xl px-6 py-6 transition-all duration-300 no-underline hover:scale-105 shadow-xl hover:shadow-2xl hover:shadow-yellow-500/30"
          >
            <span className="text-4xl md:text-5xl">🏷️</span>
            <div className="text-left">
              <span className="block text-white font-black text-lg md:text-xl mb-1">Ingatlant hirdetek</span>
              <span className="block text-gray-400 text-sm group-hover:text-white">Piacтér →</span>
            </div>
          </Link>
        </div>
        
        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm md:text-base">
          <div className="flex items-center gap-2 bg-green-500/10 backdrop-blur-xl border border-green-500/30 rounded-full px-4 py-2">
            <span className="text-green-400 text-xl">✓</span>
            <span className="text-white font-semibold">Átlátható árak</span>
          </div>
          <div className="flex items-center gap-2 bg-green-500/10 backdrop-blur-xl border border-green-500/30 rounded-full px-4 py-2">
            <span className="text-green-400 text-xl">✓</span>
            <span className="text-white font-semibold">Partneri kivitelezők</span>
          </div>
          <div className="flex items-center gap-2 bg-green-500/10 backdrop-blur-xl border border-green-500/30 rounded-full px-4 py-2">
            <span className="text-green-400 text-xl">✓</span>
            <span className="text-white font-semibold">Kontrollált folyamat</span>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
        <span className="text-xs hidden md:block">Görgess a megoldásokhoz</span>
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}
